/**
 * The names a page's books are kept under (part 36a, deep-fix r4, table BK: books belong to the account): the book store,
 * the backup log, the records of typing not stored yet, the edit lock and the tabs' channel. The device's first store
 * keeps the names it always had, so a book from before accounts stays where it is; each account's own store, and the
 * empty one shown with nobody logged in, has names of its own that none of the others' match or begin with.
 */
import { DB_NAME, EDIT_LOCK } from '@cockatrice/storage';
import { isAccountId } from '../account/wire.ts';
import type { Shelf } from '../account/shelf.ts';
import { BACKUP_DB } from '../backup/db.ts';
import { UNSAVED_PREFIX } from './unsaved.ts';

/** The names of one shelf's storage. */
export interface Space {
  readonly books: string;
  readonly backups: string;
  /** The prefix of each page's record of typing not stored yet (fix-next 1). */
  readonly unsaved: string;
  readonly editLock: string;
  readonly channel: string;
}

/** The device's first store: the names from before accounts. */
export const FIRST_SPACE: Space = Object.freeze({ books: DB_NAME, backups: BACKUP_DB, unsaved: UNSAVED_PREFIX, editLock: EDIT_LOCK, channel: 'cockatrice' });

/** The key of a shelf other than the first: its account's ID, or `none`. */
const NONE = 'none';

function keyed(key: string): Space {
  // `@` after the unsaved records' prefix, so the first store's prefix (`cockatrice.unsaved.`) never begins another's.
  return { books: `${DB_NAME}.${key}`, backups: `${BACKUP_DB}.${key}`, unsaved: `cockatrice.unsaved@${key}.`, editLock: `${EDIT_LOCK}@${key}`, channel: `cockatrice@${key}` };
}

/** BK: the names of `shelf`'s storage. */
export function spaceOf(shelf: Shelf): Space {
  switch (shelf.kind) {
    case 'first':
      return FIRST_SPACE;
    case 'own':
      return keyed(shelf.account);
    case 'none':
      return keyed(NONE);
  }
}

/**
 * BK10: the space a worker was started for, from its name (the page names it by its book store): the first store's
 * name, or an account's or `none`'s; any other name gives `none`'s, an empty store, never another account's.
 */
export function spaceOfBooks(name: string): Space {
  if (name === DB_NAME) return FIRST_SPACE;
  const key = name.startsWith(`${DB_NAME}.`) ? name.slice(DB_NAME.length + 1) : NONE;
  return isAccountId(key) ? keyed(key) : keyed(NONE);
}
